import React, { useEffect, useState } from "react";
import { LightToolTip } from "components/LightToolTip";
import * as Utils from "logic/Utils";
import { getTranslation as translate, formatShortDate } from "logic/LocaleManager";

// Externals
import { parseDate } from "devextreme/localization";
//import cn from "classnames";

// DevExtreme components
import { Menu } from 'devextreme-react';
import DataGrid, { Column } from 'devextreme-react/data-grid';

// Component styles
import classes from "./TimeOverviewWidget.module.scss";

export function TimeOverviewWidget(props) {
  const { ui, data, config } = props;

  // Using single state pattern here because of DataGrid refreshing problem
  const [singleState, setSingleState] = useState({
    currentYearIndex: 0,
    currentPeriodIndex: 0,
    currentZoomIndex: 0
  });

  const { currentYearIndex, currentPeriodIndex, currentZoomIndex } = singleState;

  const colors = ["#00AA55", "#009FD4", "#B381B3", "#939393", "#E3BC00", "#D47500", "#DC2A2A"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const zoomLevels = [
    {
      icon: "hierarchy",
      items: [
        { id: 0, text: translate("general.FullYear", "Full year") },
        { id: 1, text: translate("general.HalfYear", "Half year") },
        { id: 2, text: translate("general.QuarterYear", "Three months") },
        { id: 3, text: translate("general.SingleMonth", "One month") }
      ]
    }
  ];

  let widgetId = ui.id;
  let content = data ? JSON.parse(data.content) : null;
  let years = content ? content : [];
  let currentYear = years && years.length > 0 ? years[currentYearIndex] : null;
  let currentMonths = calcMonths();
  let titleColumn = config ? config.TitleColumn : null;
  let showCellText = false;

  const createDataSource = () => {
    return currentYear ? currentYear.Rows : [];
  }

  useEffect(() => {
    //console.log("TimeOverviewWidget data of " + ui.id + ": ", data);
  }, [data]);

  function currentPeriodDisplayText() {
    let result = "";
    switch (currentZoomIndex) {
      case 0:
        break;
      case 1:
        result = "H" + (currentPeriodIndex + 1);
        break;
      case 2:
        result = "Q" + (currentPeriodIndex + 1);
        break;
      case 3:
        let month = months[currentPeriodIndex];
        result = translate("general." + month, month);
        break;
      default:
        break;
    }
    return result + " " + (currentYear ? currentYear.Year : "");
  }

  function hasPreviousYear() {
    return currentYearIndex > 0;
  }

  function hasNextYear() {
    return currentYearIndex < years.length - 1
  }

  function hasPreviousPeriod() {
    return currentPeriodIndex > 0;
  }

  function hasNextPeriod() {
    switch (currentZoomIndex) {
      case 0:
        return false;
      case 1:
        return (currentPeriodIndex < 1)
      case 2:
        return (currentPeriodIndex < 3)
      case 3:
        return (currentPeriodIndex < 11)
      default:
        console.error("hasNextPeriod: invalid zoom level", currentZoomIndex);
        return false;
    }
  }

  function calcMonths() {
    switch (currentZoomIndex) {
      case 0:
        return months;
      case 1:
        switch (currentPeriodIndex) {
          case 0:
            return months.slice(0, 6);
          case 1:
            return months.slice(6, 12);
          default:
            console.error("calcMonths: invalid period index", currentPeriodIndex);
            break;
        }
        break;
      case 2:
        switch (currentPeriodIndex) {
          case 0:
            return months.slice(0, 3);
          case 1:
            return months.slice(3, 6);
          case 2:
            return months.slice(6, 9);
          case 3:
            return months.slice(9, 12);
          default:
            console.error("calcMonths: invalid period index", currentPeriodIndex);
            break;
        }
        break;
      case 3:
        return months.slice(currentPeriodIndex, currentPeriodIndex + 1);
      default:
        console.error("calcMonths: invalid zoom level", currentZoomIndex);
        break;
    }
  }

  function calcSumValue(rowData) {
    return currentMonths.reduce((total, m) => total + (rowData[m] ? rowData[m].Days : 0), 0);
  }

  function formatDays(days) {
    if (days === 0) return;
    if (days === 1) return days + " " + translate("general.day", "Day");
    return days + " " + translate("general.days", "Days");
  }

  function renderDayOverviewToolTip(numberOfDays, entries) {
    return (
      <div>
        {formatDays(numberOfDays)}
        {entries.map((p, i) =>
          <p key={i}>
            {formatShortDate(p.StartDate) + " - " + formatShortDate(p.StopDate)}
          </p>
        )}
      </div>
    );
  }

  const renderUnsupportedToolTipContent = () => {
    return (
      <div>
        UNSUPPORTED_YEAR_SPAN<br />
        Data with year spanning is not supported.<br />
        The number of total days represents all days including the part in the other year.
      </div>
    );
  }

  const renderYearColumnHeader = (args) => {
    return (
      <div className={classes.headerWrapper}>
        <div />
        <div>
          <i
            className={"dx-icon-chevronleft"}
            onClick={() => setSingleState(prev => ({ ...prev, currentPeriodIndex: prev.currentPeriodIndex - 1 }))}
            style={{ visibility: hasPreviousPeriod() ? "visible" : "hidden", cursor: "pointer" }}
          />
          <i
            className={"dx-icon-chevrondoubleleft"}
            onClick={() => setSingleState(prev => ({ ...prev, currentYearIndex: prev.currentYearIndex - 1 }))}
            style={{ visibility: hasPreviousYear() ? "visible" : "hidden", cursor: "pointer" }}
          />
          {currentPeriodDisplayText()}
          <i
            className={"dx-icon-chevrondoubleright"}
            onClick={() => setSingleState(prev => ({ ...prev, currentYearIndex: prev.currentYearIndex + 1 }))}
            style={{ visibility: hasNextYear() ? "visible" : "hidden", cursor: "pointer" }}
          />
          <i
            className={"dx-icon-chevronright"}
            onClick={() => setSingleState(prev => ({ ...prev, currentPeriodIndex: prev.currentPeriodIndex + 1 }))}
            style={{ visibility: hasNextPeriod() ? "visible" : "hidden", cursor: "pointer" }}
          />
        </div>
        <div>
          <Menu
            className={classes.zoomMenu}
            items={zoomLevels}
            onItemClick={(e) => {
              if (e.itemData.id >= 0) {
                setSingleState(prev => ({ ...prev, currentPeriodIndex: 0, currentZoomIndex: e.itemData.id }))
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderMonthCell = (args, month) => {
    if (!args.value) return;
    if (args.value.Message === "UNSUPPORTED_YEAR_SPAN") {
      return (
        <LightToolTip title={renderUnsupportedToolTipContent()}>
          <div>[UNSUPPORTED]</div>
        </LightToolTip>
      );
    }
    if (args.value.Days === 0) return;

    let monthNumber = months.indexOf(month) + 1;
    let referenceDate = new Date(currentYear.Year, monthNumber, 0);
    let lastDayOfMonth = referenceDate.getDate();
    let daysOfMonth = Array.from({ length: lastDayOfMonth }, (v, k) => k + 1);
    let highlightColor = Utils.colorFromText(args.data.Title, colors);
    let parsedEntries = args.value.Periods.map((p) => ({
      StartDate: parseDate(p.Start, "yyyy-MM-dd"),
      StopDate: parseDate(p.Stop, "yyyy-MM-dd")
    }));

    let wrapperStyle = {
      width: "100%",
      height: "1.5em",
      background: createBackgroundFaster()
    }

    function createBackgroundFaster() {
      let running = 0;
      let allDaysColored = daysOfMonth.map((day) => {
        let hit = parsedEntries.some((p) => p.StartDate.getDate() <= day && p.StopDate.getDate() >= day);
        let color = hit ? highlightColor : "transparent";
        let procentage = (1 / lastDayOfMonth) * 100;
        let start = running;
        running += procentage;
        return [color, " ", start, "%, ", color, " ", (running), "% "].join('');
      });
      return "linear-gradient(to right," + allDaysColored.join(", ") + ")";
    }

    // function createBackground() {
    //   let running = 0;
    //   let bg = "linear-gradient(to right,";
    //   daysOfMonth.forEach(day => {
    //     let hit = args.value.Periods.some(p => parseDate(p.Start, "yyyy-MM-dd").getDate() <= day && parseDate(p.Stop, "yyyy-MM-dd").getDate() >= day);
    //     let procentage = (1 / lastDayOfMonth) * 100;
    //     let color = hit ? highlightColor : "transparent";
    //     bg += color + " " + running + "%, " + color + " " + (running + procentage) + "%, ";
    //     running += procentage;
    //   });
    //   let result = bg.substring(0, bg.length - 2) + ")";
    //   return result;
    // }

    return (
      <LightToolTip title={renderDayOverviewToolTip(args.value.Days, parsedEntries)}>
        <div style={wrapperStyle}>
          {showCellText ? formatDays(args.value.Days) : null}
        </div>
      </LightToolTip>
    );
  };

  const renderTable = () => {
    return (
      <DataGrid
        className={classes.table}
        dataSource={createDataSource()}
        showBorders={true}
        showColumnLines={true}
        showRowLines={true}
        allowColumnResizing={true}
        columnResizingMode="widget"
        loadPanel={{ enabled: true }}
      >

        <Column
          dataField="Title"
          alignment="left"
          width="12em"
          caption={titleColumn.HeaderText}
        />

        <Column
          calculateCellValue={(args) => calcSumValue(args)}
          alignment="center"
          width="4em"
          caption="∑"
        />

        <Column
          alignment="center"
          headerCellRender={(args) => renderYearColumnHeader(args)}
        >

          {currentMonths.map((month, i) =>
            <Column
              key={i}
              cssClass="monthColumn"
              dataField={month}
              alignment="center"
              caption={translate("general." + month, month)}
              cellRender={(args) => renderMonthCell(args, month)}
              calculateSortValue={month + ".Days"}
            />
          )}

        </Column>
      </DataGrid>
    );
  };

  return config ? (
    <div className={classes.wrapper} id={widgetId}>
      {renderTable()}
    </div>
  ) : null;
}

